import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, Button, Form, Modal, Badge } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import API from '../services/api';
import Pager from './Pager';
import SearchSelect from './SearchSelect';
import { money, METHOD_LABEL } from './IngresoModal';
import { formatDay, todayDR, monthLabel, calendarDay } from '../utils/dates';
import Loading from './Loading';

// Transportes: delivery / pickup fees collected WITHOUT a factura (backend/routes/transports.js). They count as money
// received everywhere (dashboard, cierre del día, reports) but are internal: never on a factura or a DGII listing. Sales
// register the ones they collect and see their own; admins see all of them, the dashboard, and edit or delete.

const PAGE_SIZE = 15;
const METHODS = ['efectivo', 'transferencia', 'tarjeta', 'cheque'];
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const Tile = ({ label, value, sub, color }) => (
  <Card className="shadow-sm p-3 dashboard-card h-100">
    <div className="text-muted small">{label}</div>
    <h3 className="mt-2 mb-0" style={color ? { color } : undefined}>{value}</h3>
    {sub && <div className="small text-muted mt-1">{sub}</div>}
  </Card>
);
const times = (n) => `${n} ${n === 1 ? 'transporte' : 'transportes'}`;

// Add or correct one. `row` = the one being edited (admins), or null for a new one.
const TransporteModal = ({ row, onClose, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [clientId, setClientId] = useState(row?.client?._id || '');
  const [date, setDate] = useState(row ? calendarDay(row.date) : todayDR());
  const [amount, setAmount] = useState(row ? String(row.amount) : '');
  const [method, setMethod] = useState(row?.method || 'efectivo');
  const [account, setAccount] = useState(row?.account || '');
  const [notes, setNotes] = useState(row?.notes || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
    // An edit (admins) may keep an account that has since been deactivated, so it lists them all.
    API.get('/bank-accounts', { params: row ? { all: '1' } : {} }).then(r => setAccounts(r.data)).catch(() => setAccounts([]));
  }, [row]);
  // A transfer names one of the company's accounts: the active ones, plus the one an edited transporte already has.
  const accountChoices = accounts.filter(a => a.active !== false || a._id === row?.account);

  const value = Number(amount);
  const ok = value > 0 && date && date <= todayDR() && (method !== 'transferencia' || account) && notes.length <= 200;
  const submit = async () => {
    setBusy(true); setError('');
    const body = { client: clientId || null, date, amount: value, method, notes, ...(method === 'transferencia' ? { account } : {}) };
    try {
      const { data } = row ? await API.put(`/transportes/${row._id}`, body) : await API.post('/transportes', body);
      onSaved(data, !row);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar el transporte.');
      setBusy(false);
    }
  };

  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}><Modal.Title>{row ? 'Editar transporte' : 'Nuevo transporte'}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <p className="small text-muted">Lo cobrado por una entrega o un retiro sin factura. Cuenta como ingreso recibido, pero es interno: no aparece en facturas ni en los reportes de DGII.</p>
        <Form.Group className="mb-3">
          <Form.Label>Cliente <span className="text-muted small">(opcional)</span></Form.Label>
          <SearchSelect options={clients} value={clientId} getKey={c => c._id} getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
            onSelect={c => setClientId(c?._id || '')} placeholder="Buscar cliente..." emptyLabel="Sin clientes coincidentes" />
          {clientId && <Button variant="link" size="sm" className="px-0" onClick={() => setClientId('')}>Quitar cliente</Button>}
        </Form.Group>
        <Row>
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label>Monto</Form.Label>
              <Form.Control type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" autoFocus={!row} />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group className="mb-3">
              <Form.Label>Fecha</Form.Label>
              <Form.Control type="date" value={date} max={todayDR()} onChange={e => setDate(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Label>Forma de pago</Form.Label>
          <Form.Select value={method} onChange={e => setMethod(e.target.value)}>
            {METHODS.map(m => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
          </Form.Select>
        </Form.Group>
        {method === 'transferencia' && (
          <Form.Group className="mb-3">
            <Form.Label>Cuenta donde se recibió</Form.Label>
            <Form.Select value={account} onChange={e => setAccount(e.target.value)}>
              <option value="">Elige la cuenta...</option>
              {accountChoices.map(a => <option key={a._id} value={a._id}>{a.name}{a.active === false ? ' (desactivada)' : ''}</option>)}
            </Form.Select>
            {!accountChoices.length && <Form.Text className="text-muted">No hay cuentas bancarias activas: un administrador las agrega en Ingresos → Cuentas bancarias.</Form.Text>}
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label>Nota <span className="text-muted small">(opcional)</span></Form.Label>
          <Form.Control as="textarea" rows={2} maxLength={200} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ej.: entrega y retiro de cama, Higüey" />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button variant="primary" disabled={busy || !ok} onClick={submit}>{busy ? 'Guardando...' : row ? 'Guardar cambios' : 'Registrar transporte'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const TransportesTab = ({ isAdmin }) => {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // {} = new, a row = edit
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const params = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
    API.get('/transportes', { params }).then(r => setRows(r.data)).catch(err => setError(err.response?.data?.message || 'No se pudieron cargar los transportes.'));
    if (isAdmin) API.get('/transportes/summary').then(r => setSummary(r.data)).catch(() => {});
  }, [from, to, isAdmin]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, from, to]);

  const term = norm(search.trim());
  const shown = useMemo(() => (rows || []).filter(r => !term
    || [r.client?.name, r.client?.rnc, r.client?.cedula, r.notes, r.createdBy, r.accountName, METHOD_LABEL[r.method]].some(v => norm(v).includes(term))), [rows, term]);
  const shownTotal = shown.reduce((s, r) => s + r.amount, 0);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = shown.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const saved = (r, isNew) => {
    setEditing(null);
    setNotice(`${isNew ? 'Transporte registrado' : 'Transporte actualizado'}: ${money(r.amount)}${r.client?.name ? ` de ${r.client.name}` : ''}, ${formatDay(r.date)}.`);
    load();
  };
  const remove = async () => {
    setBusy(true); setError('');
    try {
      await API.delete(`/transportes/${deleting._id}`);
      setNotice(`Transporte de ${money(deleting.amount)} (${formatDay(deleting.date)}) eliminado.`);
      setDeleting(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo eliminar el transporte.');
    } finally { setBusy(false); }
  };

  const chart = summary && {
    labels: summary.monthly.map(m => monthLabel(m.month, true)),
    datasets: [{ label: 'Transportes', data: summary.monthly.map(m => m.total), backgroundColor: 'rgba(251,146,60,0.65)', borderRadius: 4 }],
  };
  const p = summary?.periods;

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success d-flex justify-content-between align-items-start">
        <span>{notice}</span>
        <button type="button" className="close" aria-label="Cerrar" onClick={() => setNotice('')}><span aria-hidden="true">&times;</span></button>
      </div>}

      {isAdmin && (
        <>
          <Row className="mb-3 g-3">
            <Col md={3} sm={6}><Tile label="Hoy" value={money(p?.day.total)} sub={p ? times(p.day.count) : 'Cargando...'} /></Col>
            <Col md={3} sm={6}><Tile label="Esta semana" value={money(p?.week.total)} sub={p ? times(p.week.count) : 'Cargando...'} /></Col>
            <Col md={3} sm={6}><Tile label="Este mes" value={money(p?.month.total)} sub={p ? times(p.month.count) : 'Cargando...'} color="var(--mm-cyan)" /></Col>
            <Col md={3} sm={6}><Tile label="Este año" value={money(p?.year.total)} sub={p ? times(p.year.count) : 'Cargando...'} /></Col>
          </Row>
          <Row className="mb-3 g-3">
            <Col lg={7}>
              <Card className="shadow-sm p-3 h-100">
                <Card.Title className="mb-2" style={{ fontSize: '1rem' }}>Transportes por mes (últimos 12)</Card.Title>
                <div style={{ height: 240 }}>
                  {chart ? <Bar data={chart} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => money(c.raw) } } } }} /> : <div className="text-muted">Cargando...</div>}
                </div>
              </Card>
            </Col>
            <Col lg={5}>
              <Card className="shadow-sm p-3 h-100">
                <Card.Title className="mb-2" style={{ fontSize: '1rem' }}>Este año</Card.Title>
                {summary ? (
                  <>
                    <div className="small text-muted mb-1">Por forma de pago</div>
                    {summary.byMethod.length ? summary.byMethod.map(m => (
                      <div key={m.method} className="d-flex justify-content-between small"><span>{METHOD_LABEL[m.method] || m.method} · {m.count}</span><span>{money(m.total)}</span></div>
                    )) : <div className="small text-muted">Nada este año.</div>}
                    <div className="small text-muted mt-3 mb-1">Clientes que más pagaron</div>
                    {summary.topClients.map(c => (
                      <div key={c.client || 'none'} className="d-flex justify-content-between small"><span className="text-truncate me-2">{c.name} · {c.count}</span><span>{money(c.total)}</span></div>
                    ))}
                    <div className="small text-muted mt-3">
                      Desde {summary.allTime.since ? formatDay(summary.allTime.since) : '—'}: {money(summary.allTime.total)} en {times(summary.allTime.count)}.
                    </div>
                  </>
                ) : <Loading small label="Cargando..." />}
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Card className="shadow-sm p-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <Card.Title className="mb-0">
            {isAdmin ? 'Transportes' : 'Tus transportes'}{' '}
            <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>· {shown.length} · {money(shownTotal)}</span>
          </Card.Title>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Control type="date" size="sm" style={{ width: 150 }} value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} title="Desde" />
            <Form.Control type="date" size="sm" style={{ width: 150 }} value={to} min={from || undefined} onChange={e => setTo(e.target.value)} title="Hasta" />
            {(from || to) && <Button size="sm" variant="link" onClick={() => { setFrom(''); setTo(''); }}>Quitar fechas</Button>}
            <Form.Control size="sm" style={{ width: 260 }} placeholder="Buscar por cliente, nota o quien lo registró..." value={search} onChange={e => setSearch(e.target.value)} />
            <Button variant="primary" size="sm" onClick={() => { setNotice(''); setEditing({}); }}><i className="bi bi-plus-lg me-1" />Nuevo transporte</Button>
          </div>
        </div>

        {rows === null ? <Loading label="Cargando transportes..." /> : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr><th>Fecha</th><th>Cliente</th><th className="text-end">Monto</th><th>Forma de pago</th><th>Nota</th><th>Registró</th><th /></tr>
              </thead>
              <tbody>
                {slice.map(r => (
                  <tr key={r._id}>
                    <td>{formatDay(r.date)}</td>
                    <td>{r.client?.name || <span className="text-muted">—</span>}</td>
                    <td className="text-end">{money(r.amount)}</td>
                    <td>{METHOD_LABEL[r.method] || r.method}{r.accountName && <div className="small text-muted">{r.accountName}</div>}</td>
                    <td className="small" style={{ maxWidth: 320 }}>{r.notes || '—'}</td>
                    <td className="small">{r.createdBy || '—'}{r.legacy?.source && <div><Badge bg="secondary" className="fw-normal">Sistema anterior</Badge></div>}</td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="outline-secondary" className="me-1" title="Recibo de transporte para el cliente" onClick={() => window.open(`/ingresos/${r._id}/print`, '_blank')}>
                        <i className="bi bi-printer" />
                      </Button>
                      {isAdmin && <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => { setNotice(''); setEditing(r); }}>Editar</Button>}
                      {isAdmin && <Button size="sm" variant="outline-danger" onClick={() => { setNotice(''); setDeleting(r); }}>Eliminar</Button>}
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">
                    {term || from || to ? 'Ningún transporte coincide.' : isAdmin ? 'Todavía no hay transportes.' : 'Todavía no has registrado transportes.'}
                  </td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        {pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">Página {current} de {pages}</small>
            <Pager page={current} pages={pages} onChange={setPage} showEnds />
          </div>
        )}
      </Card>

      {editing && <TransporteModal row={editing._id ? editing : null} onClose={() => setEditing(null)} onSaved={saved} />}
      {deleting && (
        <Modal show onHide={busy ? undefined : () => setDeleting(null)} centered>
          <Modal.Header closeButton={!busy}><Modal.Title>Eliminar transporte</Modal.Title></Modal.Header>
          <Modal.Body>
            ¿Eliminar el transporte de <strong>{money(deleting.amount)}</strong> del {formatDay(deleting.date)}{deleting.client?.name ? ` (${deleting.client.name})` : ''}?
            Deja de contar como ingreso. Queda registrado en el historial de actividad.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" disabled={busy} onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button variant="danger" disabled={busy} onClick={remove}>{busy ? 'Eliminando...' : 'Eliminar'}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default TransportesTab;
